import React, { useEffect } from 'react';
import Carousel from 'react-multi-carousel';
import 'react-multi-carousel/lib/styles.css';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { IoIosArrowBack, IoIosArrowForward } from 'react-icons/io';
import { FaBolt, FaFire, FaRegHeart } from 'react-icons/fa';
import { MdOutlinePlaylistAdd } from 'react-icons/md';
import { add_to_wishlist, messageClear } from '../../store/reducers/cardReducer';
import { add_to_my_list, messageClear as myListMessageClear } from '../../store/reducers/myListReducer';
import { useCurrency } from '../../context/CurrencyContext';
import Container from '../Container';
import Rating from '../Rating';
import toast from 'react-hot-toast';

// ─── Responsive breakpoints ───────────────────────────────────────────────────
// 6 per row on desktop — one more than the 5-column "Explore Your Interests" grid.
const responsive = {
  superLargeDesktop: {
    breakpoint: { max: 4000, min: 1280 },
    items: 6,
  },
  desktop: {
    breakpoint: { max: 1280, min: 1024 },
    items: 5,
  },
  tablet: {
    breakpoint: { max: 1024, min: 640 },
    items: 3,
  },
  mobile: {
    breakpoint: { max: 640, min: 0 },
    items: 2,
  },
};

// ─── ProductCard ─────────────────────────────────────────────────────────────
// Defined outside parent to keep a stable component reference (no remounts).
const ProductCard = ({ p, onWishlist, onMyList, formatBase }) => {
  const finalPrice =
    p.discount > 0 ? p.price - (p.price * p.discount) / 100 : p.price;
  const isLowStock = p.stock > 0 && p.stock <= 5;
  const isTrending = p.discount >= 20 && !isLowStock;

  return (
    <div className="group">
      {/* ── Image ── */}
      <Link
        to={`/product/details/${p.slug}`}
        target="_blank"
        rel="noopener noreferrer"
        className="relative block overflow-hidden bg-gray-50"
      >
        <div className="aspect-square w-full overflow-hidden">
          <img
            src={p.images?.[0]}
            alt={p.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        </div>

        {/* Discount badge */}
        {p.discount > 0 && (
          <span className="absolute left-0 top-2 rounded-r-md bg-[#E21C1B] px-1.5 py-0.5 text-[10px] font-bold leading-none text-white">
            -{p.discount}%
          </span>
        )}

        {/* Wishlist + My List overlay — appear on hover */}
        <div className="absolute right-1.5 top-2 flex flex-col gap-1.5 opacity-0 transition-all duration-150 group-hover:opacity-100">
          <button
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onWishlist(p); }}
            className="flex h-6 w-6 items-center justify-center rounded-full bg-white/90 text-gray-400 shadow-sm transition-all duration-150 hover:bg-white hover:text-[#E21C1B]"
            title="Add to Wishlist"
          >
            <FaRegHeart size={10} />
          </button>
          <button
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onMyList(p); }}
            className="flex h-6 w-6 items-center justify-center rounded-full bg-white/90 text-gray-400 shadow-sm transition-all duration-150 hover:bg-white hover:text-[#1769e0]"
            title="Add to My List"
          >
            <MdOutlinePlaylistAdd size={14} />
          </button>
        </div>

        {/* Low-stock overlay bar */}
        {isLowStock && (
          <span className="absolute inset-x-0 bottom-0 bg-black/70 py-1 text-center text-[9.5px] font-semibold text-white">
            Only {p.stock} left
          </span>
        )}
      </Link>

      {/* ── Body ── */}
      <div className="pt-1.5">
        {/* Pricing */}
        <div className="flex items-baseline gap-1.5">
          <span className="text-[13px] font-bold text-[#E21C1B] leading-none">
            {formatBase(finalPrice)}
          </span>
          {p.discount > 0 && (
            <span className="text-[10px] text-gray-400 line-through leading-none">
              {formatBase(p.price)}
            </span>
          )}
        </div>

        {/* Rating, or a trending/stock nudge when there's no rating yet */}
        <div className="mt-1 flex items-center gap-1">
          {p.rating > 0 ? (
            <>
              <div className="flex text-[8px]"><Rating ratings={p.rating} /></div>
              <span className="text-[9.5px] text-gray-400 leading-none">{p.rating.toFixed(1)}</span>
            </>
          ) : isTrending ? (
            <span className="text-[9.5px] font-semibold text-[#E21C1B] leading-none">Hot Deal</span>
          ) : (
            <span className="text-[9.5px] text-gray-400 leading-none">Limited time</span>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────
const LatestProducts = ({ products = [] }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { userInfo } = useSelector((state) => state.auth);
  const { successMessage, errorMessage } = useSelector((state) => state.card);
  const { successMessage: myListSuccessMessage, errorMessage: myListErrorMessage } = useSelector((state) => state.myList);
  const { formatBase } = useCurrency();

  // latest_product from Redux is an array-of-arrays (grouped pages); flatten it
  const flat = Array.isArray(products[0]) ? products.flat() : products;

  const handleWishlist = (p) => {
    if (!userInfo) { navigate('/login'); return; }
    dispatch(add_to_wishlist({
      userId: userInfo.id,
      productId: p._id,
      name: p.name,
      price: p.price,
      image: p.images?.[0],
      discount: p.discount,
      rating: p.rating,
      slug: p.slug,
    }));
  };

  const handleMyList = (p) => {
    if (!userInfo) { navigate('/login'); return; }
    dispatch(add_to_my_list({ userId: userInfo.id, productId: p._id }));
  };

  useEffect(() => {
    if (successMessage) { toast.success(successMessage); dispatch(messageClear()); }
    if (errorMessage)   { toast.error(errorMessage);    dispatch(messageClear()); }
  }, [successMessage, errorMessage, dispatch]);

  useEffect(() => {
    if (myListSuccessMessage) { toast.success(myListSuccessMessage); dispatch(myListMessageClear()); }
    if (myListErrorMessage)   { toast.error(myListErrorMessage);    dispatch(myListMessageClear()); }
  }, [myListSuccessMessage, myListErrorMessage, dispatch]);

  const DealHeader = () => (
    <div className="mb-5 overflow-hidden rounded-lg border border-red-100 bg-white shadow-[0_4px_18px_rgba(226,28,27,0.08)]">
      <div className="relative flex min-h-[74px] items-center justify-between gap-4 px-5 py-4 sm:px-4">
        <div className="absolute inset-y-0 left-0 w-[58%] bg-[#fff1d8]" />
        <div className="absolute inset-y-0 right-0 w-[48%] bg-[#E21C1B]" />
        <div className="absolute inset-y-0 right-[30%] w-16 skew-x-[-18deg] bg-[#ffcf21]" />

        <div className="relative flex min-w-0 items-center gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#ff6a00] text-white shadow-[0_8px_18px_rgba(255,106,0,0.22)]">
            <FaBolt size={19} />
          </span>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <h2 className="text-[22px] font-black uppercase leading-none tracking-normal text-gray-950 sm:text-[18px]">
                Latest Deals
              </h2>
              <span className="rounded-full bg-white px-2.5 py-1 text-[10px] font-extrabold uppercase leading-none text-[#E21C1B] shadow-sm">
                New Arrivals
              </span>
            </div>
            <p className="mt-1.5 text-[12px] font-medium text-gray-700">
              Fresh picks, fast-moving prices, limited stock.
            </p>
          </div>
        </div>

        <div className="relative flex shrink-0 items-center gap-2 pb-0.5">
          <span className="hidden items-center gap-1 rounded-full bg-white/95 px-3 py-1.5 text-[11px] font-extrabold uppercase text-[#E21C1B] shadow-sm md:flex">
            <FaFire size={11} />
            Hot Now
          </span>
          <Link
            to="/products"
            className="ml-1 hidden items-center gap-0.5 rounded-full bg-gray-950 px-3 py-2 text-[11px] font-bold text-white hover:bg-white hover:text-gray-950 sm:flex"
          >
            View All <IoIosArrowForward size={11} />
          </Link>
        </div>
      </div>
    </div>
  );

  const CarouselArrow = ({ onClick, direction }) => {
    const isPrevious = direction === 'previous';

    return (
      <button
        onClick={onClick}
        className={`absolute top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-800 shadow-[0_6px_18px_rgba(0,0,0,0.12)] hover:bg-[#E21C1B] hover:text-white hover:border-[#E21C1B] transition-all duration-150 ${isPrevious ? 'left-0' : 'right-0'}`}
        aria-label={isPrevious ? 'Previous products' : 'Next products'}
      >
        {isPrevious ? <IoIosArrowBack size={16} /> : <IoIosArrowForward size={16} />}
      </button>
    );
  };

  if (!flat.length) return null;

  return (
    <section className="w-full bg-white py-8 mt-2">
      <Container>
        <DealHeader />
        <Carousel
          responsive={responsive}
          autoPlay={false}
          infinite={false}
          arrows
          customLeftArrow={<CarouselArrow direction="previous" />}
          customRightArrow={<CarouselArrow direction="next" />}
          itemClass="p-2"
          containerClass=""
          transitionDuration={350}
          swipeable
          draggable
        >
          {flat.map((p, i) => (
            <ProductCard
              key={p._id || i}
              p={p}
              onWishlist={handleWishlist}
              onMyList={handleMyList}
              formatBase={formatBase}
            />
          ))}
        </Carousel>
      </Container>
    </section>
  );
};

export default LatestProducts;
